/**
 * Event Subscription Banner Component
 * Shows user's event creation limits based on subscription
 */

import { Link } from 'react-router-dom'
import {
  Box,
  Alert,
  AlertTitle,
  Button,
  LinearProgress,
  Typography,
  Skeleton,
} from '@mui/material'
import { Event, Upgrade } from '@mui/icons-material'
import { useEventSubscriptionLimits } from '../hooks/useCalendarEvents'

interface EventSubscriptionBannerProps {
  variant?: 'compact' | 'full'
  showUpgradeLink?: boolean
}

export function EventSubscriptionBanner({
  variant = 'full',
  showUpgradeLink = true,
}: EventSubscriptionBannerProps) {
  const { data, isLoading, isError } = useEventSubscriptionLimits()
  const limits = data as import('@api/types/calendarEvent').EventSubscriptionLimitsDto | undefined

  if (isLoading) {
    return (
      <Box sx={{ mb: 2 }}>
        <Skeleton variant="rectangular" height={variant === 'compact' ? 48 : 80} />
      </Box>
    )
  }

  if (isError || !limits) {
    return null
  }

  const { maxEventsPerMonth, eventsCreatedThisMonth, maxEventSize, canCreateEvent } = limits
  const progress = maxEventsPerMonth > 0 ? (eventsCreatedThisMonth / maxEventsPerMonth) * 100 : 0
  const remaining = maxEventsPerMonth - eventsCreatedThisMonth
  const isAtLimit = !canCreateEvent

  // Compact variant for inline display
  if (variant === 'compact') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Event color={isAtLimit ? 'error' : 'action'} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" color={isAtLimit ? 'error' : 'text.secondary'}>
            {eventsCreatedThisMonth}/{maxEventsPerMonth} events this month
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progress}
            color={isAtLimit ? 'error' : progress > 75 ? 'warning' : 'primary'}
            sx={{ height: 4, borderRadius: 2, mt: 0.5 }}
          />
        </Box>
        {isAtLimit && showUpgradeLink && (
          <Button
            component={Link}
            to="/subscription"
            size="small"
            variant="outlined"
            startIcon={<Upgrade />}
          >
            Upgrade
          </Button>
        )}
      </Box>
    )
  }

  // Full variant with alert
  return (
    <Alert
      severity={isAtLimit ? 'warning' : 'info'}
      icon={<Event />}
      sx={{ mb: 2 }}
      action={
        isAtLimit && showUpgradeLink ? (
          <Button
            component={Link}
            to="/subscription"
            color="inherit"
            size="small"
            startIcon={<Upgrade />}
          >
            Upgrade
          </Button>
        ) : undefined
      }
    >
      <AlertTitle>
        {isAtLimit ? 'Event Limit Reached' : 'Event Creation Limit'}
      </AlertTitle>
      <Typography variant="body2">
        You've created {eventsCreatedThisMonth} of {maxEventsPerMonth} events this month.
        {isAtLimit
          ? ' Upgrade your subscription to create more events.'
          : ` You can create ${remaining} more event${remaining !== 1 ? 's' : ''}.`}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={progress}
        color={isAtLimit ? 'warning' : progress > 75 ? 'warning' : 'primary'}
        sx={{ height: 6, borderRadius: 3, mt: 1 }}
      />
      {maxEventSize > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Max attendees per event: {maxEventSize}
        </Typography>
      )}
    </Alert>
  )
}

/**
 * Hook to check if user can create events
 */
export function useCanCreateEvent(): { canCreate: boolean; isLoading: boolean } {
  const { data, isLoading } = useEventSubscriptionLimits()
  const limits = data as import('@api/types/calendarEvent').EventSubscriptionLimitsDto | undefined
  return {
    canCreate: limits?.canCreateEvent ?? true, // Default to true if not loaded
    isLoading,
  }
}

export default EventSubscriptionBanner
